import {
  getLocationMetaActivity,
  type LocationMetaActivityState,
  convertVolumeQuantity,
  type LocationContext,
  type Shift,
} from "@ots-frontend/ots-data";

import type {OtsProgressBarTone} from "../components/OtsProgressBar";

type LocationMeta = NonNullable<LocationContext["meta"]>;
export type ActiveShiftRow = {
  id: string;
  personId: string;
  regionId: string;
  driver: string;
  startDttm: number;
  vehicle?: string;
  trailer?: string;
  freightCategory?: "WATER" | "OIL";
  freightType?: string;
  site?: string;
  activity?: LocationMetaActivityState;
  destination?: LocationMeta["destination"];
  possibleDestinations?: LocationMeta["possibleDestinations"];
  contents?: LocationMeta["contents"];
  locationDttm?: number;
  position?: {latitude: number; longitude: number};
  /** Locations before the shift started cannot establish the current site. */
  locationBeforeShift?: boolean;
};

export const LOCATION_STALE_AFTER_MS = 5 * 60_000;

/** Shift records are authoritative; GPS availability never determines whether a shift is active. */
export function buildActiveShiftRows(
  shifts: readonly Shift[],
  locations: readonly LocationContext[],
  regionId: string,
  now: number
): ActiveShiftRow[] {
  const locationsByPerson = new Map<string, LocationContext[]>();
  for (const location of locations) {
    if (location.entityType !== "PERSON") continue;
    const current = locationsByPerson.get(location.entityId) ?? [];
    current.push(location);
    locationsByPerson.set(location.entityId, current);
  }
  const seen = new Set<string>();
  return shifts
    .filter((shift) => {
      if (
        seen.has(shift.id) ||
        shift.regionId !== regionId ||
        !Number.isFinite(shift.startDttm) ||
        shift.startDttm > now ||
        (shift.endDttm != null && shift.endDttm <= now) ||
        shift.holdingRecord ||
        shift.tabletHolding
      )
        return false;
      seen.add(shift.id);
      return true;
    })
    .map((shift) => {
      const location = locationsByPerson
        .get(shift.personId)
        ?.filter((value) => !shift.database || value.tenantCode === shift.database)
        .sort((a, b) => (b.location?.dttm ?? 0) - (a.location?.dttm ?? 0))[0];
      const point = location?.location;
      const validPoint =
        point &&
        Number.isFinite(point.latitude) &&
        Number.isFinite(point.longitude) &&
        Math.abs(point.latitude) <= 90 &&
        Math.abs(point.longitude) <= 180 &&
        Number.isFinite(point.dttm) &&
        point.dttm <= now;
      const locationBeforeShift = !!validPoint && point.dttm < shift.startDttm;
      const currentShift = location?.meta?.shift?.id === shift.id;
      const activity = getLocationMetaActivity(location?.meta);
      const trailerAttributes = location?.meta?.relationships?.find((rel) => rel.entity.entityType === "TRAILER")
        ?.entity.entityAttributes;
      const contents = currentShift ? location?.meta?.contents : undefined;
      const freightType = contents
        ? contents.materialType
        : currentShift && typeof trailerAttributes?.onboardFreightType === "string"
        ? trailerAttributes.onboardFreightType
        : undefined;
      const category = contents
        ? contents.category
        : currentShift
        ? location?.meta?.work?.workType?.replace(/_HAUL$/, "")
        : undefined;
      const freightCategory =
        freightType?.toUpperCase() === "MIXED"
          ? undefined
          : category === "WATER"
          ? ("WATER" as const)
          : category === "OIL"
          ? ("OIL" as const)
          : undefined;
      return {
        id: shift.id,
        personId: shift.personId,
        regionId: shift.regionId,
        driver: shift.personName || location?.entityName || "Unknown driver",
        startDttm: shift.startDttm,
        vehicle: shift.vehicleName,
        trailer: shift.trailerName,
        freightCategory,
        freightType,
        // Context must belong to this shift; a previous shift's work is never current contents/intent.
        contents,
        activity: validPoint && !locationBeforeShift && currentShift ? activity?.state : undefined,
        destination: validPoint && !locationBeforeShift && currentShift ? location?.meta?.destination : undefined,
        possibleDestinations:
          validPoint && !locationBeforeShift && currentShift ? location?.meta?.possibleDestinations : undefined,
        site:
          validPoint && !locationBeforeShift && currentShift && activity?.onSite
            ? location.meta.geofenceEvent?.siteName
            : undefined,
        position: validPoint ? {latitude: point.latitude, longitude: point.longitude} : undefined,
        locationDttm: validPoint ? point.dttm : undefined,
        locationBeforeShift,
      };
    })
    .sort(
      (a, b) =>
        a.driver.localeCompare(b.driver, undefined, {numeric: true, sensitivity: "base"}) || a.id.localeCompare(b.id)
    );
}

export function elapsedShiftTime(start: number, now: number): string {
  const minutes = Math.max(0, Math.floor((now - start) / 60_000));
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function shiftLocationAge(row: ActiveShiftRow, now: number): string {
  if (!row.position || row.locationDttm == null) return "No recent location";
  if (row.locationBeforeShift) return "Location before this shift";
  const minutes = Math.max(0, Math.floor((now - row.locationDttm) / 60_000));
  return minutes < 1 ? "Updated just now" : `Updated ${minutes}m ago`;
}

export function isShiftLocationStale(row: ActiveShiftRow, now: number): boolean {
  return row.locationDttm == null || !!row.locationBeforeShift || now - row.locationDttm > LOCATION_STALE_AFTER_MS;
}

export function shiftLocationLabel(row: ActiveShiftRow, now: number): string {
  if (!row.position) return "Location unavailable";
  if (row.locationBeforeShift) return "Awaiting shift location";
  if (isShiftLocationStale(row, now)) return row.site ? `Last at ${row.site}` : "Last location stale";
  if (row.activity === "OFFLINE") return row.site ? `Offline · Last at ${row.site}` : "Offline";
  if (row.site) return `At ${row.site}`;
  if (row.activity === "IN_TRANSIT") return "In transit";
  if (row.activity === "STOPPED") return "Stopped · Off site";
  if (row.activity === "ON_SITE") return "At site";
  return "Off site · Status unknown";
}

export function shiftDestinationDetail(row: ActiveShiftRow, now: number): string | undefined {
  if (isShiftLocationStale(row, now) || row.activity === "OFFLINE") return undefined;
  const target = row.destination;
  if (!target || target.destination.status === "ARRIVED") return undefined;
  return `Suggested: ${target.destination.siteName}`;
}

/** Only explicit, compatible units can produce a capacity percentage. Unknown contents has no bar. */
export function shiftContentsDisplay(row: ActiveShiftRow, now: number) {
  const contents = row.contents;
  const quantity = contents?.quantity;
  if (!contents) return {label: "Contents unavailable"};
  if (
    !contents ||
    contents.state === "UNKNOWN" ||
    quantity == null ||
    !Number.isFinite(quantity) ||
    quantity < 0 ||
    !contents.quantityUom ||
    contents.calculatedDttm == null ||
    !Number.isFinite(contents.calculatedDttm) ||
    contents.calculatedDttm > now ||
    now - contents.calculatedDttm > LOCATION_STALE_AFTER_MS
  )
    return {label: "Contents unknown"};
  let capacity: number | undefined;
  if (
    contents.capacity != null &&
    contents.capacity > 0 &&
    Number.isFinite(contents.capacity) &&
    contents.capacityUom
  ) {
    try {
      capacity = convertVolumeQuantity(contents.capacityUom, contents.quantityUom, contents.capacity);
    } catch {
      /* Incompatible units are displayed as quantity only. */
    }
  }
  if (capacity != null && (!Number.isFinite(capacity) || capacity <= 0)) capacity = undefined;
  const estimatePrefix = contents.estimated ? "~" : "";
  const format = (value: number) => new Intl.NumberFormat(undefined, {maximumFractionDigits: 1}).format(value);
  const uom =
    ({barrels: "bbl", gallons: "gal", liters: "L", "cubic meters": "m³"} as Record<string, string>)[
      contents.quantityUom
    ] ?? contents.quantityUom;
  const percent = capacity == null ? undefined : Math.round((quantity / capacity) * 100);
  const fill = capacity == null ? undefined : quantity / capacity;
  const tone: OtsProgressBarTone =
    quantity === 0
      ? "neutral"
      : fill != null && fill > 1
      ? "danger"
      : fill != null && fill >= 0.9
      ? row.freightCategory === "WATER"
        ? "water-near-capacity"
        : row.freightCategory === "OIL"
        ? "oil-near-capacity"
        : "warning"
      : row.freightCategory === "WATER"
      ? "water"
      : row.freightCategory === "OIL"
      ? "oil"
      : "neutral";
  return {
    tone,
    nearCapacity: fill != null && fill >= 0.9 && fill <= 1,
    label:
      contents.state === "EMPTY"
        ? `${estimatePrefix}Empty`
        : `${estimatePrefix}${format(quantity)}${capacity == null ? "" : ` / ${format(capacity)}`} ${uom}`,
    quantity,
    capacity,
    percent,
    overCapacity: percent != null && quantity > (capacity ?? Infinity),
  };
}
